import { Reveal } from "./Motion";
import { MapPin, Mail, ArrowUpRight, Footprints } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL, WhatsAppIcon } from "@/components/site/ContactLinks";

const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Rue+Piers+93+1080+Molenbeek-Saint-Jean";

export default function Come({ onTalk }) {
  const { t } = useLang();
  return (
    <section id="venir" className="py-16 md:py-24 bg-background" data-testid="come-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-start">
          <div className="md:col-span-5">
            <Reveal>
              <div className="chip">{t("come.chip")}</div>
              <h2 className="mt-4 font-heading text-3xl md:text-5xl leading-[1.05]">
                {t("come.title_1")} <span className="text-primary">{t("come.title_molenbeek")}</span>
              </h2>
              <p className="mt-5 text-lg text-foreground/80 leading-relaxed">{t("come.subtitle")}</p>

              <div className="mt-8 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl grid place-items-center bg-muted text-primary shrink-0">
                    <MapPin className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-foreground/60">
                      {t("come.address_label")}
                    </div>
                    <div className="mt-1 font-heading text-lg">{t("come.address_value")}</div>
                    <a
                      href={MAPS_URL}
                      target="_blank"
                      rel="noreferrer noopener"
                      data-testid="come-maps-link"
                      className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-primary link-underline"
                    >
                      {t("come.maps_cta")}
                      <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                    </a>
                  </div>
                </div>

                <div className="rounded-2xl bg-muted/70 border border-border p-5" data-testid="come-access-card">
                  <div className="flex items-center gap-2.5 font-heading text-lg">
                    <Footprints className="w-5 h-5 text-primary" strokeWidth={2.4} />
                    {t("come.access_title")}
                  </div>
                  <p className="mt-2 text-sm text-foreground/75 leading-relaxed">{t("come.access_intro")}</p>
                  <ol className="mt-3 space-y-2">
                    {[1, 2, 3, 4].map((n) => (
                      <li key={n} className="flex items-start gap-2.5 text-sm text-foreground/85">
                        <span className="mt-0.5 w-5 h-5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold grid place-items-center shrink-0">
                          {n}
                        </span>
                        {t(`come.access_${n}`)}
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl grid place-items-center bg-muted text-primary shrink-0">
                    <Mail className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-foreground/60">
                      {t("come.contact_label")}
                    </div>
                    <a
                      href="mailto:contact@pazapaasbl.be"
                      data-testid="come-email-link"
                      className="mt-1 block font-heading text-lg text-primary link-underline"
                    >
                      contact@pazapaasbl.be
                    </a>
                    <a
                      href={PHONE_TEL}
                      data-testid="come-phone-link"
                      className="mt-1 block font-heading text-lg text-primary link-underline"
                    >
                      {PHONE_DISPLAY}
                    </a>
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noreferrer noopener"
                      data-testid="come-whatsapp-link"
                      className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-primary link-underline"
                    >
                      <WhatsAppIcon className="w-4 h-4" />
                      {t("contact.whatsapp_cta")}
                    </a>
                    <div className="mt-1 text-sm text-foreground/75">{t("come.contact_text")}</div>
                    <button
                      onClick={onTalk}
                      data-testid="come-talk-btn"
                      className="mt-3 pill-btn bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      {t("nav.talk")}
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal className="md:col-span-7">
            <div className="rounded-3xl overflow-hidden border border-border">
              <iframe
                title={t("come.map_alt")}
                src="https://www.openstreetmap.org/export/embed.html?bbox=4.3260%2C50.8530%2C4.3420%2C50.8620&layer=mapnik&marker=50.8574%2C4.3339"
                className="w-full h-[380px] md:h-[460px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <p className="mt-3 text-xs text-foreground/55">{t("come.map_note")}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
